import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["isInvalid"];
import React, { useEffect, useMemo, useRef } from 'react';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import { useIntl } from 'react-intl-next';
import { Editor, EditorContext, WithEditorActions } from '@atlaskit/editor-core';
import { ComposableEditor } from '@atlaskit/editor-core/composable-editor';
import * as i18nMessages from '@atlaskit/editor-core/i18n';
import { createDefaultPreset } from '@atlaskit/editor-core/preset-default';
import { usePreset } from '@atlaskit/editor-core/use-preset';
import { accessibilityUtilsPlugin } from '@atlaskit/editor-plugins/accessibility-utils';
import { alignmentPlugin } from '@atlaskit/editor-plugins/alignment';
import { batchAttributeUpdatesPlugin } from '@atlaskit/editor-plugins/batch-attribute-updates';
import { cardPlugin } from '@atlaskit/editor-plugins/card';
import { contentInsertionPlugin } from '@atlaskit/editor-plugins/content-insertion';
import { dataConsumerPlugin } from '@atlaskit/editor-plugins/data-consumer';
import { emojiPlugin } from '@atlaskit/editor-plugins/emoji';
import { gridPlugin } from '@atlaskit/editor-plugins/grid';
import { guidelinePlugin } from '@atlaskit/editor-plugins/guideline';
import { indentationPlugin } from '@atlaskit/editor-plugins/indentation';
import { insertBlockPlugin } from '@atlaskit/editor-plugins/insert-block';
import { listPlugin } from '@atlaskit/editor-plugins/list';
import { mediaPlugin } from '@atlaskit/editor-plugins/media';
import { panelPlugin } from '@atlaskit/editor-plugins/panel';
import { pasteOptionsToolbarPlugin } from '@atlaskit/editor-plugins/paste-options-toolbar';
import { rulePlugin } from '@atlaskit/editor-plugins/rule';
import { scrollIntoViewPlugin } from '@atlaskit/editor-plugins/scroll-into-view';
import { statusPlugin } from '@atlaskit/editor-plugins/status';
import { textColorPlugin } from '@atlaskit/editor-plugins/text-color';
import { toolbarListsIndentationPlugin } from '@atlaskit/editor-plugins/toolbar-lists-indentation';
import { EditorCardProvider } from '@atlaskit/link-provider';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { ProFormaIntlProvider } from '../../../../../intl-provider/ProFormaIntlProvider';
import { RichTextEditorWrapper } from './styled';
var environment = window.location.hostname === 'localhost' ? 'staging' : undefined; // Make smart links connect to staging when developing locally.
var editorCardProvider = new EditorCardProvider(environment);
var RichTextEditor = function RichTextEditor(_ref) {
  var id = _ref.id,
    value = _ref.value,
    onChange = _ref.onChange,
    actions = _ref.actions;
  var intl = useIntl();
  var lastSyncedEditorValue = useRef(value);
  useEffect(function () {
    /**
     * Due to the debounce for `handleEditorChange`, to avoid race conditions
     * we need to manually track `editorValue` with `lastSyncedEditorValue`
     * instead of using `await actions.getValue()`, in useEffect
     *  */
    if (!isEqual(lastSyncedEditorValue.current, value)) {
      lastSyncedEditorValue.current = value;
      actions.replaceDocument(value);
    }
  }, [value, actions]);

  // A proper debounce in a React component needs
  // - To use the same debounce callback
  // - With ref to the latest handleEditorChange across rerenders
  var handleEditorChange = function handleEditorChange() {
    return actions.getValue().then(function (editorValue) {
      lastSyncedEditorValue.current = editorValue;
      onChange(editorValue);
    });
  };
  var handleEditorChangeRef = useRef(handleEditorChange);
  handleEditorChangeRef.current = handleEditorChange;
  var debouncedOnChange = useMemo(function () {
    return debounce(function () {
      return handleEditorChangeRef.current();
    }, 500);
  }, []);
  var patchedMediaPlugin = function patchedMediaPlugin(_ref2) {
    var api = _ref2.api,
      config = _ref2.config;
    var mediaPluginInternal = mediaPlugin({
      api: api,
      config: config
    });
    return {
      name: 'media',
      getSharedState: mediaPluginInternal.getSharedState,
      actions: mediaPluginInternal.actions,
      nodes: mediaPluginInternal.nodes,
      pmPlugins: mediaPluginInternal.pmPlugins,
      commands: mediaPluginInternal.commands
    };
  };
  var createPreset = function createPreset() {
    return createDefaultPreset({
      featureFlags: {},
      paste: {},
      blockType: {
        lastNodeMustBeParagraph: false
      },
      appearance: 'comment',
      allowAnalyticsGASV3: true,
      preferenceToolbarAboveSelection: false,
      hyperlinkOptions: {
        lpLinkPicker: false,
        platform: 'web'
      },
      quickInsert: {
        disableDefaultItems: false,
        headless: false
      },
      selection: {
        useLongPressSelection: false
      }
    }).add(dataConsumerPlugin).add(accessibilityUtilsPlugin).add(contentInsertionPlugin).add(batchAttributeUpdatesPlugin).add(alignmentPlugin).add(textColorPlugin).add(listPlugin).add(rulePlugin).add(guidelinePlugin).add([gridPlugin, {
      shouldCalcBreakoutGridLines: false
    }]).add([patchedMediaPlugin, {
      allowMediaSingle: {
        disableLayout: true
      }
    }])
    // Required for `panelPlugin` in the case that you need to use `allowCustomPanel`
    .maybeAdd(emojiPlugin, false).add([panelPlugin, {
      useLongPressSelection: false,
      allowCustomPanel: false,
      allowCustomPanelEdit: false
    }]).add([cardPlugin, {
      provider: Promise.resolve(editorCardProvider),
      allowEmbeds: true,
      allowResizing: false
    }]).add([statusPlugin, {
      menuDisabled: false,
      allowZeroWidthSpaceAfter: true
    }]).add(indentationPlugin).add(scrollIntoViewPlugin).add([toolbarListsIndentationPlugin, {
      showIndentationButtons: false,
      allowHeadingAndParagraphIndentation: false
    }]).add([insertBlockPlugin, {
      allowTables: true,
      allowExpand: false,
      horizontalRuleEnabled: true,
      nativeStatusSupported: true,
      showElementBrowserLink: false
    }]).add(pasteOptionsToolbarPlugin);
  };
  var _usePreset = usePreset(createPreset),
    preset = _usePreset.preset;
  return /*#__PURE__*/React.createElement(ProFormaIntlProvider, {
    i18nMessages: i18nMessages,
    locale: intl.locale
  }, /*#__PURE__*/React.createElement(Box, {
    id: id
  }, fg('proforma_update_quick_insert_items') ? /*#__PURE__*/React.createElement(ComposableEditor, {
    defaultValue: value,
    onChange: debouncedOnChange,
    key: id,
    preset: preset
  }) : /*#__PURE__*/React.createElement(Editor, {
    allowAnalyticsGASV3: true,
    appearance: "comment",
    allowDate: false,
    allowExtension: {
      allowBreakout: true
    },
    allowIndentation: true,
    allowLayouts: false,
    media: {
      allowMediaSingle: {
        disableLayout: true
      },
      allowResizing: false,
      allowResizingInTables: false,
      allowLinking: false
    },
    allowPanel: true,
    allowRule: true,
    allowStatus: true,
    allowTables: false,
    allowTextAlignment: true,
    allowTextColor: true,
    allowTemplatePlaceholders: {
      allowInserting: false
    },
    defaultValue: value,
    onChange: debouncedOnChange,
    linking: {
      smartLinks: {
        provider: Promise.resolve(editorCardProvider),
        allowEmbeds: true
      }
    }
  })));
};
export var EditRichTextParagraph = function EditRichTextParagraph(_ref3) {
  var isInvalid = _ref3.isInvalid,
    props = _objectWithoutProperties(_ref3, _excluded);
  return /*#__PURE__*/React.createElement(RichTextEditorWrapper, {
    isInvalid: isInvalid
  }, /*#__PURE__*/React.createElement(EditorContext, null, /*#__PURE__*/React.createElement(WithEditorActions, {
    render: function render(actions) {
      return /*#__PURE__*/React.createElement(RichTextEditor, _extends({}, props, {
        actions: actions
      }));
    }
  })));
};